<template>
  <g>
    <g
      v-for="(barGroup, index) in groupedBars"
      :id="dimension"
      :key="index"
      :opacity="opacity(index)"
      @mouseover="onHover(index)"
      @mouseleave="hover = false"
      pointer-events="visible"
    >
      <!-- including a hidden rect for hover actions until
      pointer-events: bounding-box is supported by more browsers -->
      <rect
        v-if="hasHover"
        fill="none"
        :height="barGroup.hidden.height"
        :width="barGroup.hidden.width"
        :x="barGroup.hidden.x"
        :y="barGroup.hidden.y"
      ></rect>
      <g v-if="hasTransitions">
        <!-- Do not bind y, height and fill to avoid
        conflict between Vue and D3 controlling DOM -->
        <rect v-for="key in chartKeys"
          :key="key.name"
          :class="`${key.name}-bar`"
          :width="barGroup.width"
          :x="barGroup[key.name].x"
        ></rect>
      </g>
      <g v-else>
        <rect v-for="key in chartKeys"
          :key="key.name"
          :class="`${key.name}-bar`"
          :fill="barGroup[key.name].color"
          :height="barGroup[key.name].height"
          :width="barGroup.width"
          :x="barGroup[key.name].x"
          :y="barGroup[key.name].y"
        ></rect>
      </g>
    </g>
  </g>
</template>

<script>
import { bandScale, linearScale }
  from '../../../../../../../services/dashboard/graphs/scalesService';
import { selectAll } from 'd3';

export default {
  props: {
    // From Graph Wrapper
    data: {
      type: Array,
      required: true,
    },
    defaultChartMax: {
      type: Number,
    },
    dimensionKey: {
      type: String,
      default: 'dimension',
    },
    contentDimensions: {
      type: Object,
      required: true,
    },
    marginTop: {
      type: Number,
      default: 0,
    },
    // Custom to Grouped Bars
    customChartMax: {
      type: Number,
    },
    highlightedArea: {
      type: Array,
    },
    barWidth: {
      type: Number,
      default: 24,
    },
    barPadding: {
      type: Number,
      default: 8,
    },
    dimension: {
      type: String,
    },
    hasTransitions: {
      type: Boolean,
      default: false,
    },
    hasHover: {
      type: Boolean,
      default: false,
    },
    barColors: { // name: value, e.g. primary: '#FFFFFF'
      type: Object,
      required: true,
    },
    chartKeys: { // { name: 'primary', value: 'base'}
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      hover: false,
      hoverIndex: 0,
    };
  },
  computed: {
    chartMax() {
      return this.customChartMax || this.defaultChartMax;
    },
    xScale() {
      const range = { min: 0, max: this.contentDimensions.width };
      const args = [ this.data, this.dimensionKey, range ];

      return bandScale(...args);
    },
    yScale() {
      const range = { min: this.contentDimensions.height, max: this.marginTop };
      const domain = { min: 0, max: this.chartMax };

      return linearScale(range, domain);
    },
    groupedBars() {
      const barClusterWidth = this.barWidth * this.numberOfBars
        + this.barPadding * (this.numberOfBars - 1);
      const keyValues = this.chartKeys.map(key => key.value);

      return this.data.map(barData => {
        const xScaleResult = this.xScale(barData[this.dimensionKey]);
        const yScaleResults = keyValues.map(keyValue => this.yScale(barData[keyValue] || 0));
        const barClusterX = xScaleResult + barClusterWidth / 2;
        const barClusterY = Math.min(...yScaleResults);
        const offset = this.calculateTooltipOffset(barData, xScaleResult);

        const groupedBarsResult = {
          xLabel: barData[this.dimensionKey].toUpperCase(),
          xTransform: barClusterX,
          width: this.barWidth,
          ...this.generateBarData(xScaleResult, yScaleResults),
          hidden: {
            height: this.contentDimensions.height - barClusterY,
            width: barClusterWidth,
            x: xScaleResult,
            y: barClusterY,
          },
          tooltipPosition: {
            top: barClusterY,
            left: this.contentDimensions.marginLeft +
              (offset || barClusterX),
            dimension: barData[this.dimensionKey],
          },
        };

        return groupedBarsResult;
      });
    },
    numberOfBars() {
      return this.chartKeys.length;
    },
  },
  methods: {
    onHover(index) {
      this.hover = true;
      this.hoverIndex = index;
      this.$emit('updateHoveredBar', this.groupedBars[index]['tooltipPosition']);
    },
    opacity(index) {
      if (this.highlightedArea) {
        return index < this.highlightedArea[0]
          || index > this.highlightedArea[1] - 1
          ? .25
          : 1;
      }

      return this.hasHover && this.hover && index !== this.hoverIndex
        ? .25
        : 1;
    },
    generateBarData(x, yValuesArray) {
      return this.chartKeys.reduce((acc, chartKey, idx) => {
        acc[chartKey.name] = {
          color: this.barColors[chartKey.name],
          height: this.contentDimensions.height - yValuesArray[idx],
          x: x + (this.barWidth * idx) + (this.barPadding * idx),
          y: yValuesArray[idx],
        };

        return acc;
      }, {});
    },
    calculateTooltipOffset(barData, x) {
      const indexesWithData = this.chartKeys.reduce((acc, chartKey, index) => {
        if (barData[chartKey.value] !== null) {
          acc.push(index);
        }

        return acc;
      }, []);

      if (!indexesWithData.length) {
        return null;
      }

      const firstIndex = indexesWithData[0];
      const firstBarOffset = this.offset(x, firstIndex);

      if (indexesWithData.length === 1) {
        return firstBarOffset;
      } else {
        const lastIndex = indexesWithData.pop();
        const lastBarOffset = this.offset(x, lastIndex);

        return (lastBarOffset + firstBarOffset) / 2;
      }
    },
    offset(x, multiplier) {
      return x + ((this.barWidth + this.barPadding) * multiplier)
        + this.barWidth / 2;
    },
    mountTransitions() {
      this.chartKeys.forEach((key) => {
        selectAll(`#${this.dimension} .${key.name}-bar`)
          .data(this.groupedBars)
        // Set initial height to 0 to have bars grow
        // from bottom of chart instead of top
          .attr('fill', d => { return d[key.name].color; })
          .attr('y', _ => { return this.yScale(0); })
          .attr('height', _ => { return 0; })
          .transition()
          .duration(750)
          .attr('y',  d => { return d[key.name].y; })
          .attr('height', d => { return d[key.name].height; })
          .delay(function(d, i) {
            return i * 150;
          });
      });
    },
    updateTransitions() {
      this.chartKeys.forEach((key) => {
        selectAll(`#${this.dimension} .${key.name}-bar`)
          .data(this.groupedBars)
          .transition()
          .duration(500)
          .attr('y',  d => { return d[key.name].y; })
          .attr('height', d => { return d[key.name].height; })
          .attr('fill', d => { return d[key.name].color; });
      });
    },
  },
  mounted() {
    if (this.hasTransitions) {
      this.mountTransitions();
    }
  },
  updated() {
    if (this.hasTransitions) {
      this.updateTransitions();
    }
  },
  watch: {
    hover() {
      this.$emit('updateHover', this.hover);
    },
  },
};
</script>
